import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Search, X, Loader2 } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import api, { mediaUrl } from "../lib/api";
import useSeo from "../hooks/useSeo";

function PostCard({ p, index }) {
  return (
    <Reveal delay={index * 60}>
      <Link to={`/blog/${p.slug}`} className="group block h-full rounded-2xl border border-gold/20 bg-white overflow-hidden hover:shadow-xl hover:border-gold transition-all duration-300">
        <div className="relative h-48 overflow-hidden">
          <img src={mediaUrl(p.image)} alt={p.heroImageAlt || p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <span className="absolute top-3 left-3 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-ink">{p.category}</span>
        </div>
        <div className="p-6">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Calendar size={13} /> {p.date}</span>
          <h3 className="mt-2 font-display text-lg text-ink leading-snug">{p.title}</h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.excerpt}</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark group-hover:gap-2.5 transition-all">
            Read more <ArrowUpRight size={15} />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  useSeo({
    title: "Blog \u2014 Elite Vending | Smarter workplace vending insights",
    description: "Tips, trends and practical guides on stocking, servicing and getting the most from your vending machine.",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/blog/posts");
        setPosts(res.data);
      } catch (e) {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(posts.map((p) => p.category)))],
    [posts]
  );

  const isFiltering = query.trim() !== "" || category !== "All";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      const matchCat = category === "All" || p.category === category;
      const matchQuery =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(q));
      return matchCat && matchQuery;
    });
  }, [posts, query, category]);

  const [featured, ...rest] = filtered;

  return (
    <main>
      <PageHero
        eyebrow="Blog"
        title="Insights on smarter workplace vending"
        subtitle="Tips, trends and practical guides on stocking, servicing and getting the most from your vending machine."
      />

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search + Categories */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:justify-between">
            <div className="relative w-full lg:max-w-sm">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold-dark" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles..."
                className="h-12 pl-11 pr-10 rounded-full border-gold/30 focus-visible:ring-gold bg-white"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center h-7 w-7 rounded-full text-muted-foreground hover:bg-gold-soft hover:text-ink transition-colors"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2.5">
              {categories.map((c) => {
                const active = category === c;
                return (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                      active ? "bg-ink text-gold" : "bg-gold-soft/60 text-ink hover:bg-gold hover:text-ink"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          {loading ? (
            <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-gold-dark" size={34} /></div>
          ) : (
            <>
              <p className="mt-6 text-sm text-muted-foreground">
                {filtered.length === 0
                  ? "No articles found"
                  : `Showing ${filtered.length} article${filtered.length > 1 ? "s" : ""}`}
                {category !== "All" && ` in ${category}`}
                {query.trim() && ` for “${query.trim()}”`}
              </p>

              {filtered.length === 0 && (
                <div className="mt-10 rounded-2xl border border-dashed border-gold/40 bg-gold-soft/30 p-14 text-center">
                  <h3 className="font-display text-xl text-ink">Nothing matched your search</h3>
                  <p className="mt-2 text-muted-foreground">Try a different keyword or clear your filters.</p>
                  <button
                    onClick={() => { setQuery(""); setCategory("All"); }}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-6 h-11 font-semibold text-ink hover:bg-gold-dark transition-colors"
                  >
                    Reset filters
                  </button>
                </div>
              )}

              {!isFiltering && featured && (
                <Reveal className="mt-10">
                  <Link to={`/blog/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch rounded-2xl border border-gold/20 overflow-hidden bg-white shadow-sm hover:shadow-xl hover:border-gold transition-all duration-300">
                    <div className="relative min-h-[280px] overflow-hidden">
                      <img src={mediaUrl(featured.image)} alt={featured.heroImageAlt || featured.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-8 lg:p-10 flex flex-col justify-center">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="rounded-full bg-gold px-3 py-1 font-semibold uppercase tracking-widest text-ink">{featured.category}</span>
                        <span className="flex items-center gap-1.5 text-muted-foreground"><Calendar size={13} /> {featured.date}</span>
                      </div>
                      <h2 className="mt-4 font-display text-2xl sm:text-3xl text-ink leading-snug">{featured.title}</h2>
                      <p className="mt-3 text-[15px] text-muted-foreground leading-relaxed">{featured.excerpt}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 font-semibold text-gold-dark group-hover:gap-2.5 transition-all">
                        Read article <ArrowUpRight size={16} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              )}

              {filtered.length > 0 && (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(isFiltering ? filtered : rest).map((p, i) => (
                    <PostCard key={p.slug} p={p} index={i} />
                  ))}
                </div>
              )}
            </>
          )}

          <div className="mt-14 rounded-2xl bg-ink p-8 text-center">
            <h3 className="font-display text-2xl text-white">Ready for a machine that never runs empty?</h3>
            <p className="mt-2 text-white/60">Book a free site visit and we'll confirm eligibility in minutes.</p>
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-7 h-12 font-semibold text-ink hover:bg-gold-light transition-colors"
            >
              Book Free Site Visit <ArrowUpRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
